import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { invitesApi, money } from '@/lib/invitesApi';
import { Coins, Star, Check, Receipt, Sparkle } from '@phosphor-icons/react';

export default function BillingPage() {
  const { token } = useAuth();
  const [params] = useSearchParams();
  const checkoutParam = params.get('checkout');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(() => invitesApi.billing(token).then(setData).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!checkoutParam || checkoutParam === 'cancel') return;
    invitesApi.checkoutStatus(checkoutParam, token).then((r) => {
      setNote(r.granted ? 'Payment applied — thank you!' : 'Payment received.');
      load(); setTimeout(() => setNote(''), 4000);
    }).catch(() => {});
  }, [checkoutParam, token, load]);

  const buyCredits = async (pack_key) => {
    setBusy('credits');
    try { const r = await invitesApi.checkoutCredits({ pack_key, origin_url: window.location.origin }, token); window.location.href = r.url; }
    finally { setBusy(''); }
  };
  const subscribe = async (plan) => {
    setBusy('sub');
    try { const r = await invitesApi.checkoutSubscription({ plan, origin_url: window.location.origin }, token); window.location.href = r.url; }
    finally { setBusy(''); }
  };
  const cancel = async () => { await invitesApi.cancelSub(token); load(); setNote('Subscription will end at the period close.'); };

  if (!data) return <div className="min-h-screen bg-background"><Navbar /><div className="max-w-4xl mx-auto p-8"><div className="h-40 bg-secondary/50 rounded-2xl animate-pulse" /></div></div>;

  const pr = data.pricing;
  return (
    <div className="min-h-screen bg-background" data-testid="billing-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-foreground mb-1">Billing & Invitations plan</h1>
        <p className="text-sm text-muted-foreground mb-6">Manage your plan, credits, and purchase history.</p>
        {note && <div className="mb-5 text-sm bg-primary/10 text-primary rounded-lg px-3 py-2">{note}</div>}

        {/* Snapshot */}
        <div className="grid sm:grid-cols-3 gap-3 mb-8">
          <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Coins size={20} weight="fill" /></span>
            <div><div className="text-xl font-display font-semibold text-foreground">{data.credit_balance}</div><div className="text-[11px] text-muted-foreground">credits</div></div>
          </div>
          <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Star size={20} weight="fill" /></span>
            <div><div className="text-xl font-display font-semibold text-foreground">{data.pro ? 'Pro' : 'Free'}</div><div className="text-[11px] text-muted-foreground">{data.pro ? `up to ${pr.pro.guest_cap}/event` : `up to ${pr.free.guest_cap} guests`}</div></div>
          </div>
          <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Sparkle size={20} weight="fill" /></span>
            <div><div className="text-xl font-display font-semibold text-foreground">{(data.event_entitlements || []).length}</div><div className="text-[11px] text-muted-foreground">upgraded events</div></div>
          </div>
        </div>

        {/* Pro */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/40 rounded-2xl border border-primary/20 p-5 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Star size={16} weight="fill" className="text-primary" /> Pro subscription</p>
              <p className="text-sm text-muted-foreground">Unlimited premium events up to {pr.pro.guest_cap} guests — no credits, no per-event fees.</p>
            </div>
            {data.pro ? (
              <button onClick={cancel} className="text-sm font-semibold text-muted-foreground border border-border rounded-lg px-4 h-10 hover:bg-secondary/60">Cancel</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => subscribe('monthly')} disabled={busy === 'sub'} className="text-sm font-semibold border border-border bg-card rounded-lg px-4 h-10 hover:bg-secondary/60">{money(pr.pro.plans[1].amount, pr.currency)}/mo</button>
                <button onClick={() => subscribe('annual')} disabled={busy === 'sub'} className="text-sm font-semibold bg-primary text-primary-foreground rounded-lg px-4 h-10 shadow-soft hover:bg-primary/90">{money(pr.pro.plans[0].amount, pr.currency)}/yr</button>
              </div>
            )}
          </div>
        </div>

        {/* Credit packs */}
        <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5"><Coins size={15} className="text-primary" /> Buy credits (for premium extras)</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {pr.credit_packs.map((p) => (
            <button key={p.key} onClick={() => buyCredits(p.key)} disabled={busy === 'credits'} className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 text-center hover:border-primary/50 card-lift">
              <div className="text-2xl font-display font-semibold text-foreground">{p.credits}</div>
              <div className="text-[11px] text-muted-foreground mb-2">credits</div>
              <div className="text-sm font-semibold text-primary">{money(p.amount, pr.currency)}</div>
            </button>
          ))}
        </div>

        {/* Per-event tiers reference */}
        <p className="text-sm font-semibold text-foreground mb-3">Per-event upgrades</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {pr.event_tiers.map((t) => (
            <div key={t.key} className="bg-card rounded-2xl border border-border/70 shadow-soft p-4 text-center">
              <div className="text-sm font-semibold text-foreground">{t.label}</div>
              <div className="text-sm text-primary font-semibold mt-1">{money(t.amount, pr.currency)}</div>
              <div className="text-[10px] text-muted-foreground mt-1">Buy from any event’s builder</div>
            </div>
          ))}
        </div>

        {/* History */}
        <p className="text-sm font-semibold text-foreground mb-3 flex items-center gap-1.5"><Receipt size={15} className="text-primary" /> Purchase history</p>
        <div className="bg-card rounded-2xl border border-border/70 shadow-soft divide-y divide-border/50">
          {(data.purchases || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No purchases yet.</p>}
          {(data.purchases || []).map((p) => (
            <div key={p.session_id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground capitalize">{p.type.replace('invite_', '').replace('_', ' ')}{p.tier_key ? ` · ${p.tier_key}` : ''}{p.plan ? ` · ${p.plan}` : ''}{p.credits ? ` · ${p.credits} credits` : ''}</p>
                <p className="text-[11px] text-muted-foreground">{(p.created_at || '').slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{money(p.amount, p.currency)}</span>
                <Check size={14} className="text-emerald-600" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
